import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, ArrowLeft, Building2, MapPin, Phone, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    company_name: "",
    street: "",
    zip_code: "",
    city: "",
    country: "Deutschland",
    vat_id: "",
    phone: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;
      const response = await axios.post(`${API}${endpoint}`, payload);

      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      toast.success(isLogin ? "Willkommen zurück!" : "Konto erfolgreich erstellt!");
      onLogin(response.data.user);
      // Navigation is handled by <Navigate> in App.js after user state is set
      // Fallback for production environments where React state update may not trigger navigation
      setTimeout(() => {
        if (window.location.pathname !== "/dashboard") {
          window.location.href = "/dashboard";
        }
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        {/* Back to Landing */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm text-[#919aa3] hover:text-[#405189] transition-colors"
            data-testid="back-to-landing"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </button>
        </div>

        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src="/logo.png" alt="quofon.de" className="h-28 object-contain" />
        </div>

        <Card className="bg-white border-[#e8e8e8] shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-semibold text-[#495057]">
              {isLogin ? "Willkommen zurück" : "Konto erstellen"}
            </CardTitle>
            <CardDescription className="text-[#919aa3]">
              {isLogin ? "Melden Sie sich in Ihrem Konto an" : "Erstellen Sie ein neues Konto mit Ihren Unternehmensdaten"}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  {/* Personal Info */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#495057]">Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#919aa3]" />
                      <Input id="name" value={formData.name} onChange={set("name")}
                        placeholder="Max Mustermann" required
                        className="pl-10 bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="name-input" />
                    </div>
                  </div>

                  {/* Company Info */}
                  <div className="pt-2 pb-1">
                    <p className="text-xs font-semibold text-[#919aa3] uppercase tracking-wider">Unternehmensdaten</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-[#495057]">Firmenname *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#919aa3]" />
                      <Input id="company_name" value={formData.company_name} onChange={set("company_name")}
                        placeholder="Firma GmbH" required
                        className="pl-10 bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="company-name-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="street" className="text-[#495057]">Straße & Hausnummer *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#919aa3]" />
                      <Input id="street" value={formData.street} onChange={set("street")}
                        placeholder="Musterstraße 1" required
                        className="pl-10 bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="street-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="zip_code" className="text-[#495057]">PLZ *</Label>
                      <Input id="zip_code" value={formData.zip_code} onChange={set("zip_code")}
                        placeholder="12345" required
                        className="bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="zip-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-[#495057]">Ort *</Label>
                      <Input id="city" value={formData.city} onChange={set("city")}
                        placeholder="Berlin" required
                        className="bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="city-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-[#495057]">Land</Label>
                      <Input id="country" value={formData.country} onChange={set("country")}
                        placeholder="Deutschland"
                        className="bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="country-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vat_id" className="text-[#495057]">USt-IdNr.</Label>
                      <Input id="vat_id" value={formData.vat_id} onChange={set("vat_id")}
                        placeholder="DE123456789"
                        className="bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="vat-input" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#495057]">Telefon</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#919aa3]" />
                      <Input id="phone" value={formData.phone} onChange={set("phone")}
                        placeholder="+49 123 456789"
                        className="pl-10 bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                        data-testid="phone-input" />
                    </div>
                  </div>

                  <div className="pt-2 pb-1">
                    <p className="text-xs font-semibold text-[#919aa3] uppercase tracking-wider">Zugangsdaten</p>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#495057]">E-Mail *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#919aa3]" />
                  <Input id="email" type="email" value={formData.email} onChange={set("email")}
                    placeholder="name@beispiel.de" required
                    className="pl-10 bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                    data-testid="email-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#495057]">Passwort *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#919aa3]" />
                  <Input id="password" type={showPassword ? "text" : "password"}
                    value={formData.password} onChange={set("password")}
                    placeholder="••••••••" required
                    className="pl-10 pr-10 bg-white border-[#e8e8e8] text-[#495057] placeholder:text-[#b0b7bf]"
                    data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#919aa3] hover:text-[#495057]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading}
                className="w-full bg-[#405189] hover:bg-[#354270] text-white font-medium py-5 mt-6"
                data-testid="submit-btn">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Wird verarbeitet...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {isLogin ? "Anmelden" : "Registrieren"}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button type="button" onClick={() => setIsLogin(!isLogin)}
                className="text-[#919aa3] hover:text-[#495057] text-sm transition-colors">
                {isLogin ? (
                  <>Noch kein Konto? <span className="text-[#405189] font-medium">Registrieren</span></>
                ) : (
                  <>Bereits registriert? <span className="text-[#405189] font-medium">Anmelden</span></>
                )}
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[#919aa3] text-xs mt-8">
          © {new Date().getFullYear()} quofon.de. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  );
}
